import { useState, useEffect, useMemo } from 'react';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { sanitizeToNumeric, withStyles } from 'vopay';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import type { TransactionType, UserRoles } from 'vopay';
import useFetchApprovalUsers from '@/hooks/useApi/useFetchApprovalUsers';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import usePostCreateUserRole from '@/hooks/useApi/usePostCreateUserRole';
import usePostDeleteUserRole from '@/hooks/useApi/usePostDeleteUserRole';
import usePostCreateUserRolePermission from '@/hooks/useApi/usePostCreateUserRolePermission';
import usePostDeleteUserRolePermission from '@/hooks/useApi/usePostDeleteUserRolePermission';
import usePostUpdateUserRolePermission from '@/hooks/useApi/usePostUpdateUserRolePermission';
import usePostCreateAccountUser from '@/hooks/useApi/usePostCreateAccountUser';
import { useSaveAccountUserPermissions } from '@/hooks/useApi/useSaveAccountUserPermissions';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import ApproverCardEdit, { type ApproverData, type PaymentRailGroup } from './ApproverCardEdit';
import styleSheet from './ApproversEdit.module.scss';

const cn = withStyles(styleSheet);

interface ApproversEditProps {
  bearer?: string;
  accountId?: string;
  approvers: UserRoles[];
  activeTransactionTypeIDs: string[];
  linkedAccountsMap?: Record<string, string[]>;
  hideCancel?: boolean;
  onCancel: () => void;
  onSave: () => Promise<void>;
}

const ApproversEdit = ({
  bearer,
  accountId,
  approvers,
  activeTransactionTypeIDs,
  hideCancel = false,
  onCancel,
  onSave,
}: ApproversEditProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Roles.Approvers.Edit',
  });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const {
    response: { Country, subAccountPermissions },
  } = useClientData();
  const [, setLoading] = useDataFetchIndicator();
  const { response: allUsers, fetchApprovalUsers } = useFetchApprovalUsers({ bearer });
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [, , createUserRole] = usePostCreateUserRole({});
  const [, , deleteUserRole] = usePostDeleteUserRole({});
  const [, , createUserRolePermission] = usePostCreateUserRolePermission({});
  const [, , deleteUserRolePermission] = usePostDeleteUserRolePermission({});
  const [, , updateUserRolePermission] = usePostUpdateUserRolePermission({});
  const [, , createAccountUserAsync] = usePostCreateAccountUser({});
  const { saveAccountUserPermissions } = useSaveAccountUserPermissions();
  const [subAccountsRes, , , fetchSubAccountsAsync] = useFetchSubAccounts({});

  const [currentApprovers, setCurrentApprovers] = useState<ApproverData[]>([]);
  const [originalApprovers, setOriginalApprovers] = useState<ApproverData[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchApprovalUsers();
    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
    if (subAccountPermissions) {
      void fetchSubAccountsAsync({
        ActiveOnly: true,
        ShowToken: true,
        ...(accountId ? { SubaccountID: accountId } : {}),
      });
    }
  }, []);

  // Convert approvers to grouped format
  useEffect(() => {
    if (approvers && approvers.length > 0) {
      const grouped = groupApproversByUsername(approvers);
      setCurrentApprovers(grouped);
      setOriginalApprovers(JSON.parse(JSON.stringify(grouped)));
    } else {
      setCurrentApprovers([createEmptyApprover()]);
      setOriginalApprovers([]);
    }
  }, [approvers]);

  const groupApproversByUsername = (userRoles: UserRoles[]): ApproverData[] => {
    const grouped: Record<string, UserRoles[]> = {};

    userRoles.forEach((role) => {
      if (!grouped[role.Username]) {
        grouped[role.Username] = [];
      }
      grouped[role.Username].push(role);
    });

    return Object.entries(grouped).map(([username, roles]) => {
      const amountGroups: Record<string, TransactionType[]> = {};
      const hasNoLimitMap: Record<string, boolean> = {};

      roles.forEach((role) => {
        const key = role.MaxAmount || 'NO_LIMIT';
        if (!amountGroups[key]) {
          amountGroups[key] = [];
          hasNoLimitMap[key] = !role.MaxAmount || role.MaxAmount === '';
        }
        if (!amountGroups[key].includes(role.TransactionType)) {
          amountGroups[key].push(role.TransactionType);
        }
      });

      const paymentRailGroups: PaymentRailGroup[] = Object.entries(amountGroups).map(([amount, rails], index) => ({
        id: `${username}-${index}`,
        paymentRails: rails,
        amount: hasNoLimitMap[amount] ? '' : amount,
        hasNoLimit: hasNoLimitMap[amount],
      }));

      return {
        username,
        paymentRailGroups,
        addToAllSubAccounts: roles.some((r) => r.ApplyToAllSubaccounts),
      };
    });
  };

  const createEmptyApprover = (): ApproverData => ({
    username: '',
    paymentRailGroups: [{ id: `new-${Date.now()}`, paymentRails: [], amount: '', hasNoLimit: true }],
    addToAllSubAccounts: false,
  });

  const paymentRailOptions = useMemo(() => {
    if (!transactionTypesResponse?.TransactionTypes) return [];
    const typesList = Object.values(transactionTypesResponse.TransactionTypes);
    return activeTransactionTypeIDs.map((id) => {
      const type = typesList.find((t) => t.TransactionType === id);
      if (!type) {
        console.error('workflow ID', id, 'does not have an associated TransactionType');
        return { value: '' as TransactionType, label: '' as TransactionType };
      }
      return { value: type.TransactionType, label: type.TransactionType };
    });
  }, [transactionTypesResponse?.TransactionTypes, activeTransactionTypeIDs]);

  const getAvailableUsers = (excludeUsernames: string[]) =>
    allUsers.filter((user) => !excludeUsernames.includes(user.Username));

  const getAvailablePaymentRails = (approverIndex: number, groupIndex: number) => {
    const approver = currentApprovers[approverIndex];
    const selectedRails = approver.paymentRailGroups
      .filter((_, idx) => idx !== groupIndex)
      .flatMap((group) => group.paymentRails);
    return paymentRailOptions.filter((option) => !selectedRails.includes(option.value));
  };

  const handleApproverChange = (index: number, newUsername: string) => {
    const newApprovers = [...currentApprovers];
    newApprovers[index] = { ...newApprovers[index], username: newUsername };
    setCurrentApprovers(newApprovers);
    validateApprover(index, newApprovers[index]);
  };

  const handlePaymentRailsChange = (approverIndex: number, groupIndex: number, newRails: TransactionType[]) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups[groupIndex].paymentRails = newRails;
    setCurrentApprovers(newApprovers);
    validatePaymentRailGroup(approverIndex, groupIndex, newApprovers[approverIndex].paymentRailGroups[groupIndex]);
  };

  const handleAmountChange = (approverIndex: number, groupIndex: number, newAmount: string) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups[groupIndex].amount = newAmount;
    setCurrentApprovers(newApprovers);
    validatePaymentRailGroup(approverIndex, groupIndex, newApprovers[approverIndex].paymentRailGroups[groupIndex]);
  };

  const handleNoLimitChange = (approverIndex: number, groupIndex: number, hasNoLimit: boolean) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups[groupIndex].hasNoLimit = hasNoLimit;
    setCurrentApprovers(newApprovers);
    validatePaymentRailGroup(approverIndex, groupIndex, newApprovers[approverIndex].paymentRailGroups[groupIndex]);
  };

  const handleAddToAllSubAccountsChange = (index: number, value: boolean) => {
    const updated = [...currentApprovers];
    updated[index] = { ...updated[index], addToAllSubAccounts: value };
    setCurrentApprovers(updated);
  };

  const handleRemoveApprover = async (index: number) => {
    const approver = currentApprovers[index];
    const isExisting = originalApprovers.some((a) => a.username === approver.username);

    if (isExisting) {
      const message = subAccountPermissions
        ? t('removeConfirmMessageSubAccounts', {
            defaultValue: 'This approver will be removed from all associated accounts',
          })
        : t('removeConfirmMessage', { defaultValue: 'This approver will be removed from the account.' });

      const result = await alertDialog.caution(t('removeConfirmTitle', { defaultValue: 'Remove Approver?' }), message, {
        confirmLabel: t('removeConfirmButton', { defaultValue: 'Remove Approver' }),
        abortLabel: t('cancelButton', { defaultValue: 'Cancel' }),
      });

      if (result !== 'confirm') return;
    }

    setCurrentApprovers(currentApprovers.filter((_, i) => i !== index));
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach((key) => {
      if (key.startsWith(`approver-${index}-`)) delete newErrors[key];
    });
    setErrors(newErrors);
  };

  const handleAddPaymentRailGroup = (approverIndex: number) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups.push({
      id: `${newApprovers[approverIndex].username}-${Date.now()}`,
      paymentRails: [],
      amount: '',
      hasNoLimit: true,
    });
    setCurrentApprovers(newApprovers);
  };

  const handleRemovePaymentRailGroup = (approverIndex: number, groupIndex: number) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups = newApprovers[approverIndex].paymentRailGroups.filter(
      (_, i) => i !== groupIndex,
    );
    setCurrentApprovers(newApprovers);
    const errorKey = `approver-${approverIndex}-group-${groupIndex}`;
    const newErrors = { ...errors };
    delete newErrors[`${errorKey}-rails`];
    delete newErrors[`${errorKey}-amount`];
    setErrors(newErrors);
  };

  const handleAddApprover = () => {
    setCurrentApprovers([...currentApprovers, createEmptyApprover()]);
  };

  const handleNewApproverUser = async (email: string) => {
    setLoading(true);
    try {
      const createRes = await createAccountUserAsync({ EmailAddress: email, SendEmail: false });
      const userAlreadyExists = !createRes.Success && createRes.ErrorMessage?.toLowerCase().includes('already exist');

      if (!createRes.Success && !userAlreadyExists) {
        alertDialog.error(
          t('saveErrorTitle', { defaultValue: 'Error' }),
          createRes.ErrorMessage ||
            t('saveErrorMessage', { defaultValue: 'Some settings have failed to save. Please try again.' }),
          { abortLabel: t('closeButton', { defaultValue: 'Close' }) },
        );
        return;
      }

      await saveAccountUserPermissions({ EmailAddress: email, ApproveTransactions: true });

      await fetchApprovalUsers();
    } catch (err) {
      alertDialog.error(
        t('saveErrorTitle', { defaultValue: 'Error' }),
        err instanceof Error
          ? err.message
          : t('saveErrorMessage', { defaultValue: 'Some settings have failed to save. Please try again.' }),
        { abortLabel: t('closeButton', { defaultValue: 'Close' }) },
      );
    } finally {
      setLoading(false);
    }
  };

  const validateApprover = (index: number, approver: ApproverData): boolean => {
    const newErrors = { ...errors };
    const errorKey = `approver-${index}-username`;
    if (!approver.username) {
      newErrors[errorKey] = 'Please select a user';
      setErrors(newErrors);
      return false;
    }
    delete newErrors[errorKey];
    setErrors(newErrors);
    return true;
  };

  const validatePaymentRailGroup = (approverIndex: number, groupIndex: number, group: PaymentRailGroup): boolean => {
    const newErrors = { ...errors };
    const railsKey = `approver-${approverIndex}-group-${groupIndex}-rails`;
    const amountKey = `approver-${approverIndex}-group-${groupIndex}-amount`;
    let isValid = true;

    if (group.paymentRails.length === 0) {
      newErrors[railsKey] = 'Please select at least one payment rail';
      isValid = false;
    } else {
      delete newErrors[railsKey];
    }

    if (!group.hasNoLimit && (!group.amount || parseFloat(group.amount) <= 0)) {
      newErrors[amountKey] = 'Amount must be greater than 0';
      isValid = false;
    } else {
      delete newErrors[amountKey];
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAll = (): boolean => {
    let isValid = true;
    const newErrors: Record<string, string> = {};

    currentApprovers.forEach((approver, approverIndex) => {
      if (!approver.username) {
        newErrors[`approver-${approverIndex}-username`] = 'Please select a user';
        isValid = false;
      }

      approver.paymentRailGroups.forEach((group, groupIndex) => {
        if (group.paymentRails.length === 0) {
          newErrors[`approver-${approverIndex}-group-${groupIndex}-rails`] = 'Please select at least one payment rail';
          isValid = false;
        }
        if (!group.hasNoLimit && (!group.amount || parseFloat(group.amount) <= 0)) {
          newErrors[`approver-${approverIndex}-group-${groupIndex}-amount`] = 'Amount must be greater than 0';
          isValid = false;
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  const isFormValid = (): boolean => {
    if (Object.keys(errors).length > 0) return false;
    return currentApprovers.every((approver) => {
      if (!approver.username) return false;
      return approver.paymentRailGroups.every((group) => {
        if (group.paymentRails.length === 0) return false;
        if (!group.hasNoLimit && (!group.amount || parseFloat(group.amount) <= 0)) return false;
        return true;
      });
    });
  };

  const handleSave = async () => {
    if (!validateAll()) {
      await alertDialog.error(
        t('validationErrorTitle', { defaultValue: 'Validation Error' }),
        t('validationErrorMessage', { defaultValue: 'Please fix all errors before saving.' }),
        { abortLabel: t('closeButton', { defaultValue: 'Close' }) },
      );
      return;
    }

    setLoading(true);

    try {
      // Main account always includes all valid approvers
      const currentsForMainAccount = currentApprovers.filter((a) => a.username);

      // Delete from sub-accounts: deleted approvers who previously had addToAllSubAccounts
      const toRemoveFromSubs = originalApprovers.filter(
        (oa) => oa.addToAllSubAccounts && !currentApprovers.some((ca) => ca.username === oa.username),
      );

      // Push to sub-accounts: newly checked approvers (unchecked→checked, or brand new with checkbox on)
      const toSyncToSubs = currentsForMainAccount.filter((ca) => {
        if (!ca.addToAllSubAccounts) return false;
        const original = originalApprovers.find((oa) => oa.username === ca.username);
        return !original || !original.addToAllSubAccounts;
      });

      // Also re-sync existing checked approvers whose permissions changed
      const toUpdateOnSubs = currentsForMainAccount.filter((ca) => {
        if (!ca.addToAllSubAccounts) return false;
        const original = originalApprovers.find((oa) => oa.username === ca.username);
        return original && original.addToAllSubAccounts;
      });

      // Build all parallel work: main account + sub-account operations
      const allPromises: Promise<void>[] = [processApproverChanges(originalApprovers, currentsForMainAccount, bearer)];

      if (subAccountPermissions && subAccountsRes?.Subaccounts) {
        const subAccountTokenMap: Record<string, string> = Object.fromEntries(
          subAccountsRes.Subaccounts.map((sa) => [sa.AccountID, sa.Token ?? '']),
        );

        for (const subAccount of subAccountsRes.Subaccounts) {
          const subBearer = subAccountTokenMap[subAccount.AccountID];
          if (!subBearer) continue;

          allPromises.push(
            (async () => {
              // Delete permissions for removed approvers who were on sub-accounts
              for (const approver of toRemoveFromSubs) {
                for (const group of approver.paymentRailGroups) {
                  for (const rail of group.paymentRails) {
                    await deleteUserRolePermission(
                      { Username: approver.username, Role: 'approver', TransactionType: rail },
                      subBearer,
                    );
                  }
                }

                // TODO: transaction/approval/user/roles records only exist on the main account —
                // confirm whether sub-account role records are needed before re-enabling.
                // await deleteUserRole({ Username: approver.username, Role: 'approver' }, bearer);
                // TODO: re-enable when account user permissions flow is confirmed
                // await saveAccountUserPermissions(
                //   { EmailAddress: approver.username, ApproveTransactions: false },
                //   bearer,
                // );
              }

              // Create/update permissions for newly-checked approvers on this sub-account
              for (const approver of toSyncToSubs) {
                const createUserRes = await createAccountUserAsync(
                  { EmailAddress: approver.username, SendEmail: false },
                  subBearer,
                );
                const userAlreadyExists =
                  !createUserRes.Success && createUserRes.ErrorMessage?.toLowerCase().includes('already exist');
                if (!createUserRes.Success && !userAlreadyExists) continue;

                await saveAccountUserPermissions(
                  { EmailAddress: approver.username, ApproveTransactions: true },
                  subBearer,
                );

                const original = originalApprovers.find((oa) => oa.username === approver.username);
                await updateApproverPermissions(
                  approver,
                  original ?? { ...approver, paymentRailGroups: [] },
                  subBearer,
                );
              }

              // Update permissions for existing checked approvers whose permissions changed
              for (const approver of toUpdateOnSubs) {
                const original = originalApprovers.find((oa) => oa.username === approver.username);
                if (original) await updateApproverPermissions(approver, original, subBearer);
              }
            })(),
          );
        }
      }

      const results = await Promise.allSettled(allPromises);
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) throw new Error('One or more operations failed');

      toast.success(t('saveSuccessMessage', { defaultValue: 'Approver settings saved' }));
    } catch (error) {
      alertDialog.error(
        t('saveErrorTitle', { defaultValue: 'Error' }),
        t('saveErrorMessage', { defaultValue: 'Some settings have failed to save. Please try again.' }),
        { abortLabel: t('closeButton', { defaultValue: 'Close' }) },
      );
    } finally {
      setLoading(false);
      onSave();
    }
  };

  const processApproverChanges = async (originals: ApproverData[], currents: ApproverData[], bearer?: string) => {
    const originalUsernames = originals.map((a) => a.username);
    const currentUsernames = currents.filter((a) => a.username).map((a) => a.username);
    const deletedUsernames = originalUsernames.filter((u) => !currentUsernames.includes(u));

    let hasError = false;

    for (const username of deletedUsernames) {
      const originalApprover = originals.find((a) => a.username === username);

      if (originalApprover) {
        for (const group of originalApprover.paymentRailGroups) {
          for (const rail of group.paymentRails) {
            const deletePermResult = await deleteUserRolePermission(
              { Username: username, Role: 'approver', TransactionType: rail },
              bearer,
            );
            if (!deletePermResult.Success) {
              hasError = true;
              console.error(`Couldn't delete user role permission: ${deletePermResult.ErrorMessage}`);
            }
          }
        }
      }

      const result = await deleteUserRole({ Username: username, Role: 'approver' }, bearer);
      if (!result.Success) {
        hasError = true;
        console.error(`Couldn't delete user role: ${result.ErrorMessage}`);
      }
      // TODO: transaction/approval/user/roles records only exist on the main account —
      // confirm whether sub-account role records are needed before re-enabling.
      // else { await deleteUserRole({ Username: username, Role: 'approver' }, bearer); }
    }

    for (const approver of currents) {
      if (!approver.username) continue;

      const originalApprover = originals.find((a) => a.username === approver.username);

      if (!originalApprover) {
        if (bearer) {
          const createUserRes = await createAccountUserAsync(
            { EmailAddress: approver.username, SendEmail: false },
            bearer,
          );
          const userAlreadyExists =
            !createUserRes.Success && createUserRes.ErrorMessage?.toLowerCase().includes('already exist');
          if (!createUserRes.Success && !userAlreadyExists) {
            hasError = true;
            console.error(`Couldn't create account user on sub-account: ${createUserRes.ErrorMessage}`);
            continue;
          }
          await saveAccountUserPermissions({ EmailAddress: approver.username, ApproveTransactions: true }, bearer);

          const createRoleResult = await createUserRole({ Username: approver.username, Role: 'approver' }, bearer);

          const roleAlreadyExists =
            !createRoleResult.Success && createRoleResult.ErrorMessage?.toLowerCase().includes('already exist');

          if (!createRoleResult.Success && !roleAlreadyExists) {
            hasError = true;
            console.error(`Couldn't create user role on sub-account: ${createRoleResult.ErrorMessage}`);
            continue;
          }
        }

        if (!bearer) {
          const createUserRes = await createAccountUserAsync({ EmailAddress: approver.username, SendEmail: false });
          const userAlreadyExists =
            !createUserRes.Success && createUserRes.ErrorMessage?.toLowerCase().includes('already exist');
          if (!createUserRes.Success && !userAlreadyExists) {
            hasError = true;
            console.error(`Couldn't create account user on main account: ${createUserRes.ErrorMessage}`);
            continue;
          }
          await saveAccountUserPermissions({ EmailAddress: approver.username, ApproveTransactions: true });

          const createRoleResult = await createUserRole({
            Username: approver.username,
            Role: 'approver',
          });
          const roleAlreadyExists =
            !createRoleResult.Success && createRoleResult.ErrorMessage?.toLowerCase().includes('already exist');
          if (!createRoleResult.Success && !roleAlreadyExists) {
            hasError = true;
            console.error(`Couldn't create user role: ${createRoleResult.ErrorMessage}`);
            continue;
          }
        }
        // TODO: transaction/approval/user/roles records only exist on the main account —
        // confirm whether sub-account role records are needed before re-enabling.
        // else { await createUserRole({ Username: approver.username, Role: 'approver', ApplyToAllSubaccounts: approver.addToAllSubAccounts }, bearer); }
        for (const group of approver.paymentRailGroups) {
          for (const rail of group.paymentRails) {
            const createPermResult = await createUserRolePermission(
              {
                Username: approver.username,
                Role: 'approver',
                TransactionType: rail,
                MaxAmount: group.hasNoLimit ? undefined : Number(sanitizeToNumeric(group.amount)),
                SetNoMaxAmount: group.hasNoLimit,
                ApplyToAllSubaccounts: approver.addToAllSubAccounts,
              },
              bearer,
            );
            if (!createPermResult.Success) {
              if (createPermResult.ErrorMessage?.toLowerCase().includes('already exist')) {
                const updateResult = await updateUserRolePermission(
                  {
                    Username: approver.username,
                    Role: 'approver',
                    TransactionType: rail,
                    MaxAmount: group.hasNoLimit ? undefined : Number(sanitizeToNumeric(group.amount)),
                    SetNoMaxAmount: group.hasNoLimit,
                    ApplyToAllSubaccounts: approver.addToAllSubAccounts,
                  },
                  bearer,
                );
                if (!updateResult.Success) {
                  console.error(`Couldn't update user role permission on fallback: ${updateResult.ErrorMessage}`);
                  hasError = true;
                }
              } else {
                console.error(`Couldn't create user role permission: ${createPermResult.ErrorMessage}`);
                hasError = true;
              }
            }
          }
        }
      } else {
        const updateError = await updateApproverPermissions(approver, originalApprover, bearer);
        if (updateError) hasError = true;
      }
    }

    if (hasError) throw new Error('One or more operations failed');
  };

  const updateApproverPermissions = async (
    current: ApproverData,
    original: ApproverData,
    bearer?: string,
  ): Promise<boolean> => {
    let hasError = false;

    const originalPermissions = new Map<string, { amount: string; hasNoLimit: boolean }>();
    original.paymentRailGroups.forEach((group) => {
      group.paymentRails.forEach((rail) => {
        originalPermissions.set(rail, { amount: group.amount, hasNoLimit: group.hasNoLimit });
      });
    });

    const currentPermissions = new Map<string, { amount: string; hasNoLimit: boolean }>();
    current.paymentRailGroups.forEach((group) => {
      group.paymentRails.forEach((rail) => {
        currentPermissions.set(rail, { amount: group.amount, hasNoLimit: group.hasNoLimit });
      });
    });

    for (const [rail] of originalPermissions) {
      if (!currentPermissions.has(rail)) {
        const result = await deleteUserRolePermission(
          { Username: current.username, Role: 'approver', TransactionType: rail },
          bearer,
        );
        if (!result.Success) {
          console.error(`Couldn't delete user role permission: ${result.ErrorMessage}`);
          hasError = true;
        }
      }
    }

    for (const [rail, config] of currentPermissions) {
      const originalConfig = originalPermissions.get(rail);
      const permParams = {
        Username: current.username,
        Role: 'approver' as const,
        TransactionType: rail,
        MaxAmount: config.hasNoLimit ? undefined : Number(sanitizeToNumeric(config.amount)),
        SetNoMaxAmount: config.hasNoLimit,
        ApplyToAllSubaccounts: current.addToAllSubAccounts,
      };

      if (!originalConfig) {
        const result = await createUserRolePermission(permParams, bearer);
        if (!result.Success) {
          if (result.ErrorMessage?.toLowerCase().includes('already exist')) {
            const updateResult = await updateUserRolePermission(permParams, bearer);
            if (!updateResult.Success) {
              console.error(`Couldn't update user role permission on fallback: ${updateResult.ErrorMessage}`);
              hasError = true;
            }
          } else {
            console.error(`Couldn't create user role permission: ${result.ErrorMessage}`);
            hasError = true;
          }
        }
      } else if (
        originalConfig.amount !== config.amount ||
        originalConfig.hasNoLimit !== config.hasNoLimit ||
        original.addToAllSubAccounts !== current.addToAllSubAccounts
      ) {
        const result = await updateUserRolePermission(permParams, bearer);
        if (!result.Success) {
          if (result.ErrorMessage?.toLowerCase().includes('does not exist')) {
            const createResult = await createUserRolePermission(permParams, bearer);
            if (!createResult.Success) {
              console.error(`Couldn't create user role permission on fallback: ${createResult.ErrorMessage}`);
              hasError = true;
            }
          } else {
            console.error(`Couldn't update user role permission: ${result.ErrorMessage}`);
            hasError = true;
          }
        }
      }
    }

    return hasError;
  };

  return (
    <div className={cn('container')}>
      <div className={cn('instructions')}>
        {t('instructions', {
          defaultValue:
            'These users approve transactions depending on their assigned payment rails and maximum approval amount. Each maximum approval amount is tied to a payment rail.',
        })}
      </div>

      <div className={cn('approversList')}>
        {paymentRailOptions &&
          currentApprovers.map((approver, approverIndex) => {
            const otherUsernames = currentApprovers
              .filter((_, i) => i !== approverIndex)
              .map((a) => a.username)
              .filter(Boolean);
            const availableUsers = getAvailableUsers(otherUsernames);

            return (
              <ApproverCardEdit
                key={approverIndex}
                approver={approver}
                approverIndex={approverIndex}
                availableUsers={availableUsers.map((user) => ({ label: user.Username, value: user.Username }))}
                paymentRailOptions={paymentRailOptions}
                errors={errors}
                onApproverChange={(username) => handleApproverChange(approverIndex, username)}
                onPaymentRailsChange={(groupIndex, rails) => handlePaymentRailsChange(approverIndex, groupIndex, rails)}
                onAmountChange={(groupIndex, amount) => handleAmountChange(approverIndex, groupIndex, amount)}
                onNoLimitChange={(groupIndex, hasNoLimit) => handleNoLimitChange(approverIndex, groupIndex, hasNoLimit)}
                onRemove={() => handleRemoveApprover(approverIndex)}
                onAddPaymentRailGroup={() => handleAddPaymentRailGroup(approverIndex)}
                onRemovePaymentRailGroup={(groupIndex) => handleRemovePaymentRailGroup(approverIndex, groupIndex)}
                getAvailablePaymentRails={(groupIndex) => getAvailablePaymentRails(approverIndex, groupIndex)}
                onNewUser={handleNewApproverUser}
                showAccountSelection={!!subAccountPermissions}
                onAddToAllSubAccountsChange={(value) => handleAddToAllSubAccountsChange(approverIndex, value)}
              />
            );
          })}
      </div>

      <div className={cn('actions')}>
        <Button onClick={handleAddApprover} size="small" iconLeft="add">
          {t('addApproverButton', { defaultValue: 'Additional Approver' })}
        </Button>
        <div className={cn('actionButtons')}>
          {!hideCancel && (
            <Button onClick={onCancel} size="small">
              {t('cancelButton', { defaultValue: 'Cancel' })}
            </Button>
          )}
          <Button onClick={handleSave} size="small" colorScheme="primary" disabled={!isFormValid()}>
            {t('saveButton', { defaultValue: 'Save' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApproversEdit;
